/**
 * Member Layout Component
 * 
 * Shared responsive layout for all member pages with sidebar, header, and footer
 */

import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/utils/auth';
import apiClient from '@/utils/api';
import {
  LayoutDashboard,
  CreditCard,
  FolderOpen,
  Bell,
  LogOut,
  Menu,
  User,
  Edit,
  ChevronDown,
  Users,
  Building2
} from 'lucide-react';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'payments', label: 'Payments', icon: CreditCard, path: '/dashboard/payments' },
  { id: 'beneficiaries', label: 'Beneficiaries', icon: Users, path: '/dashboard/beneficiaries' },
  { id: 'providers', label: 'Providers', icon: Building2, path: '/dashboard/providers' },
  { id: 'documents', label: 'Documents', icon: FolderOpen, path: '/dashboard/documents' },
  { id: 'notifications', label: 'Notifications', icon: Bell, path: '/dashboard/notifications' },
  { id: 'profile', label: 'My Profile', icon: User, path: '/dashboard/profile' },
];

const NavContent = ({ currentPage, onItemClick, notificationCount = 0 }) => {
  return (
    <nav className="space-y-1">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = currentPage === item.id;
        return (
          <Link
            key={item.id}
            to={item.path}
            onClick={onItemClick}
            className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
            data-testid={`nav-${item.id}`}
          >
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{item.label}</span>
            </div>
            {item.id === 'notifications' && notificationCount > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                isActive ? 'bg-primary-foreground text-primary' : 'bg-primary text-primary-foreground'
              }`}>
                {notificationCount}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
};

export const MemberSidebar = ({ currentPage, notificationCount, branding }) => {
  return (
    <aside className="hidden lg:flex w-64 bg-card border-r border-border sticky top-0 h-screen overflow-y-auto p-6 flex-col">
      <div className="flex items-center gap-3 mb-6">
        {branding?.logo_url && branding?.use_logo_in_header ? (
          <img src={branding.logo_url} alt={branding?.app_name || 'Logo'} className="h-8 w-auto" />
        ) : (
          <h2 className="text-xl font-heading font-bold text-foreground">
            {branding?.app_name || 'MemberHub'}
          </h2>
        )}
      </div>
      <div className="flex-1">
        <NavContent currentPage={currentPage} notificationCount={notificationCount} />
      </div>
    </aside>
  );
};

export const MobileNav = ({ currentPage, notificationCount, branding }) => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <div className="flex flex-col h-full">
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              {branding?.logo_url && branding?.use_logo_in_header ? (
                <img src={branding.logo_url} alt={branding?.app_name || 'Logo'} className="h-8 w-auto" />
              ) : (
                <h2 className="text-xl font-heading font-bold">{branding?.app_name || 'MemberHub'}</h2>
              )}
            </div>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            <NavContent 
              currentPage={currentPage} 
              onItemClick={() => setOpen(false)} 
              notificationCount={notificationCount}
            />
          </div>
          <div className="p-4 border-t">
            <div className="mb-3 px-4">
              <p className="text-sm font-medium">{user?.name || 'Member'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <div className="space-y-2">
              <Link to="/dashboard/profile" onClick={() => setOpen(false)}>
                <Button variant="outline" className="w-full justify-start">
                  <User className="w-4 h-4 mr-2" />
                  My Profile
                </Button>
              </Link>
              <Button variant="outline" className="w-full justify-start text-destructive hover:text-destructive" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const MemberHeader = ({ title, currentPage, notificationCount, branding }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = (user?.name || 'M').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <header className="bg-card border-b border-border py-3 px-4 md:py-4 md:px-8 flex justify-between items-center sticky top-0 z-40">
      <div className="flex items-center gap-3">
        <MobileNav currentPage={currentPage} notificationCount={notificationCount} branding={branding} />
        <h1 className="text-lg md:text-2xl font-heading font-bold text-foreground truncate">{title}</h1>
      </div>
      <div className="flex items-center gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2" data-testid="profile-dropdown">
              {user?.picture ? (
                <img src={user.picture} alt="Profile" className="w-8 h-8 rounded-full object-cover" data-testid="member-avatar-image" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary" data-testid="member-avatar-initials">
                  {initials}
                </div>
              )}
              <span className="hidden sm:block text-sm">
                Welcome, <span className="font-medium">{user?.name || 'Member'}</span>
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name || 'Member'}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/dashboard/profile" className="cursor-pointer" data-testid="menu-my-profile">
                <User className="w-4 h-4 mr-2" />
                My Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/profile/edit" className="cursor-pointer" data-testid="menu-edit-profile">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout} 
              className="text-destructive focus:text-destructive cursor-pointer"
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export const MemberFooter = ({ branding }) => {
  const currentYear = new Date().getFullYear();
  const footerHtml = branding?.footer_html;
  
  if (footerHtml) {
    return (
      <footer 
        className="bg-card border-t border-border py-4 px-4 md:px-8 text-center text-sm text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(footerHtml) }}
      />
    );
  }
  
  return (
    <footer className="bg-card border-t border-border py-4 px-4 md:px-8 text-center text-sm text-muted-foreground">
      <p>© {currentYear} {branding?.app_name || 'MemberHub'}. All rights reserved.</p>
    </footer>
  );
};

const MemberLayout = ({ children, currentPage, title, notificationCount = 0 }) => {
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      const response = await apiClient.get('/settings/branding');
      setBranding(response.data);
    } catch (error) {
      setBranding({ app_name: 'MemberHub' });
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <MemberSidebar currentPage={currentPage} notificationCount={notificationCount} branding={branding} />
      <div className="flex-1 flex flex-col min-w-0">
        <MemberHeader title={title} currentPage={currentPage} notificationCount={notificationCount} branding={branding} />
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-x-hidden">
          {children}
        </main>
        <MemberFooter branding={branding} />
      </div>
    </div>
  );
};

export default MemberLayout;
